import { Plus } from 'lucide-react'
import type { LicenseStatus, LicenseType, Organisation } from '../../types'
import { LICENSE_STATUSES, LICENSE_TYPES } from './helpers'
import type { LicensesFilters } from './use-licenses-tab-state'

interface LicensesFilterBarProps {
  filters: LicensesFilters
  organisations: Organisation[]
  onCreate: () => void
}

const SELECT_CLASS =
  'px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100'

function StatusFilter({ filters }: { filters: LicensesFilters }): React.ReactElement {
  return (
    <select
      value={filters.filterStatus}
      onChange={(event) => {
        filters.setFilterStatus(event.target.value as LicenseStatus | '')
      }}
      className={SELECT_CLASS}
    >
      <option value="">All Statuses</option>
      {LICENSE_STATUSES.map((status) => (
        <option key={status.value} value={status.value}>
          {status.label}
        </option>
      ))}
    </select>
  )
}

function TypeFilter({ filters }: { filters: LicensesFilters }): React.ReactElement {
  return (
    <select
      value={filters.filterType}
      onChange={(event) => {
        filters.setFilterType(event.target.value as LicenseType | '')
      }}
      className={SELECT_CLASS}
    >
      <option value="">All Types</option>
      {LICENSE_TYPES.map((type) => (
        <option key={type.value} value={type.value}>
          {type.label}
        </option>
      ))}
    </select>
  )
}

interface OrgFilterProps {
  filters: LicensesFilters
  organisations: Organisation[]
}

function OrgFilter({ filters, organisations }: OrgFilterProps): React.ReactElement {
  return (
    <select
      value={filters.filterOrg}
      onChange={(event) => {
        filters.setFilterOrg(event.target.value)
      }}
      className={SELECT_CLASS}
    >
      <option value="">All Organisations</option>
      {organisations.map((org) => (
        <option key={org.id} value={org.id}>
          {org.name}
        </option>
      ))}
    </select>
  )
}

export function LicensesFilterBar(props: LicensesFilterBarProps): React.ReactElement {
  const { filters, organisations, onCreate } = props
  return (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-64 flex-1">
        <input
          type="text"
          placeholder="Search by license key or organisation..."
          value={filters.searchQuery}
          onChange={(event) => {
            filters.setSearchQuery(event.target.value)
          }}
          className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 placeholder-stone-400 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-100"
        />
      </div>
      <StatusFilter filters={filters} />
      <TypeFilter filters={filters} />
      <OrgFilter filters={filters} organisations={organisations} />
      <button
        onClick={onCreate}
        className="inline-flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94]"
      >
        <Plus className="h-4 w-4" />
        Create License
      </button>
    </div>
  )
}
